;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { analytics } from '@/lib/analytics/posthog'

/**
 * Normalizes an email so it is a stable, universal distinct id. MUST match the backend's
 * normalization (Laravel lowercases + trims the email) so client and server events land on the
 * SAME PostHog person — otherwise `User@x.com` and `user@x.com` split into two people and campaign
 * attribution / customer value break.
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

/**
 * Identifies the current visitor by their (normalized) email. Call as early as a real email is
 * known so anonymous pre-identify events — pageviews carrying the UTM/click-id attribution — merge
 * into this person. Pass whatever person properties are available; PostHog keeps first-touch
 * attribution (`$initial_*`) automatically.
 */
export function identifyByEmail(email: string, properties?: Record<string, unknown>): void {
  const normalized = normalizeEmail(email)
  if (!normalized) {
    return
  }
  // Drop undefined values so optional props (phone, name, …) can be passed freely.
  const clean = Object.fromEntries(Object.entries(properties ?? {}).filter(([, value]) => value !== undefined))
  analytics.identify(normalized, { email: normalized, ...clean })
}

const COUNTRY_DIAL_CODES: Record<string, string> = { NL: '31', BE: '32', DE: '49' }

/**
 * Best-effort normalization of a phone number to E.164 (e.g. `+31612345678`) for the best ad-pixel
 * match quality. Defaults bare/national numbers to the site's country (NL). Returns undefined when
 * empty or implausibly short/long. The ad platforms normalize further on their side.
 */
export function normalizePhone(
  raw?: string,
  defaultCountry: keyof typeof COUNTRY_DIAL_CODES = 'NL',
): string | undefined {
  if (!raw) {
    return undefined
  }
  const hasPlus = raw.trim().startsWith('+')
  let digits = raw.replace(/\D/g, '')
  if (!digits) {
    return undefined
  }
  const dial = COUNTRY_DIAL_CODES[defaultCountry]
  if (hasPlus) {
    // already international
  } else if (digits.startsWith('00')) {
    digits = digits.slice(2)
  } else if (digits.startsWith('0')) {
    digits = dial + digits.slice(1)
  } else if (!digits.startsWith(dial)) {
    digits = dial + digits
  }
  return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : undefined
}

/**
 * Splits a single free-text full name into first + last for pixel advanced matching (the forms
 * that only collect a full name — contact, demo). First token is the first name, the rest is the
 * last name.
 */
/**
 * The phone number as it is stored *on the person*, which is deliberately not the shape the ad
 * pixels take.
 *
 * `normalizePhone()` returns E.164 with a leading `+`, because Google's enhanced conversions ask
 * for exactly that. But `person.properties.phone` is written by both this application and the
 * Manager, and the Manager writes digits only (`App\Support\PhoneNumber`). Two writers with two
 * formats means the property flips shape depending on who touched the person last.
 *
 * Meta's CAPI destination strips the `+` before hashing, so both forms happen to produce the same
 * key — but a property whose value depends on write order is a coincidence waiting to stop being
 * one. This is the Manager's format, and the Manager is the one that also writes it from webhooks
 * where no browser is involved.
 */
export function phoneForPerson(raw?: string): string | undefined {
  return normalizePhone(raw)?.replace(/^\+/, '')
}

export function splitFullName(full?: string): { firstName?: string; lastName?: string } {
  const parts = full?.trim().split(/\s+/).filter(Boolean) ?? []
  if (parts.length === 0) {
    return {}
  }
  if (parts.length === 1) {
    return { firstName: parts[0] }
  }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
}
